import React, { useEffect } from "react";
import "../../../UpcomingHourlyComponent/UpcomingHourlyBookingTable/HourlyFloatBookingModal/HourlyFloatBookingModal.css";
import "./EditingFloatBookingModal.css";
import useEditingFloat from "./Hooks/useEditingFloat";
import Loader from "../../../../../Loader/Loader";
import BookingInfoHeader from "./BookingInfoHeader";
import EditingBookingSummary from "./EditingBookingSummary";
import RoleAccordion from "./RoleAccordion";
import { GetToFloatEditorAPI } from "../../../../../utils/APIs/hourlyAndEditingBookingsApis";

// ─── Role config — add/remove roles here only ─────────────────────────────────
const ROLES = [
    {
        key:          "editor",
        title:        "Editors",
        fetchListAPI: GetToFloatEditorAPI,
        fetchAllAPI:  GetToFloatEditorAPI,
        fetchSortAPI: null,
        showFilters:  true,
    },
];

// ─── Modal ────────────────────────────────────────────────────────────────────
const EditingFloatBookingModal = ({ booking, isOpen, onClose, refetchData }) => {
    const {
        services,
        loadingAddBooking,
        selectedPackageIndex,
        setSelectedPackageIndex,
        handleRoleSelectionChange,
        handleSubmit,
        handleRoleSubmit,
        resetAll,
        allPackagesSelections,
        refreshVersion,
    } = useEditingFloat({ booking, isOpen, onClose, refetchData });

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    if (!isOpen) return null;
    if (loadingAddBooking) return <Loader />;

    const cleanId = (id) => {
        if (!id) return "";
        if (typeof id === "object") return id._id ? id._id.toString() : id.toString();
        return id.toString();
    };

    const rawPackages = booking?.videoPackages || [];

    // ─── Aggregate Packages by Type ───
    const packages = [];
    const standardPkgs = rawPackages.filter(p => (p.type || p.planCategory || "").toString().trim().toLowerCase() === "standard");
    const premiumPkgs = rawPackages.filter(p => (p.type || p.planCategory || "").toString().trim().toLowerCase() === "premium");

    const fallbackPrice = booking?.bookingAmount || booking?.finalAmount || booking?.totalAmount || 0;

    if (standardPkgs.length > 0) {
        const calcPrice = standardPkgs.reduce((sum, p) => sum + (p.price || 0), 0);
        packages.push({
            type: "Standard",
            videosCount: standardPkgs.reduce((sum, p) => sum + (p.videosCount || 0), 0),
            price: calcPrice > 0 ? calcPrice : (premiumPkgs.length === 0 ? fallbackPrice : fallbackPrice),
            isAggregated: true,
            assignedEditors: standardPkgs.flatMap(p => (p.assignedEditors || []).map(cleanId)),
            invitedEditors: standardPkgs.flatMap(p => (p.invitedEditors || []).map(cleanId)),
        });
    }
    if (premiumPkgs.length > 0) {
        const calcPrice = premiumPkgs.reduce((sum, p) => sum + (p.price || 0), 0);
        packages.push({
            type: "Premium",
            videosCount: premiumPkgs.reduce((sum, p) => sum + (p.videosCount || 0), 0),
            price: calcPrice > 0 ? calcPrice : (standardPkgs.length === 0 ? fallbackPrice : fallbackPrice),
            isAggregated: true,
            assignedEditors: premiumPkgs.flatMap(p => (p.assignedEditors || []).map(cleanId)),
            invitedEditors: premiumPkgs.flatMap(p => (p.invitedEditors || []).map(cleanId)),
        });
    }

    if (packages.length === 0) {
        packages.push({
            type: "Custom",
            videosCount: booking?.videosCount || booking?.numberOfVideos || 1,
            price: fallbackPrice,
            isAggregated: true,
            assignedEditors: booking?.assignedEditors?.length > 0 
                ? booking.assignedEditors.map(cleanId) 
                : booking?.assignedPhotographer 
                    ? [cleanId(booking.assignedPhotographer)] 
                    : [],
            invitedEditors: (booking?.invitedEditors || []).map(cleanId),
        });
    }

    return (
        <div className="hourly-float-photographer-modal-overlay">
            <div className="hourly-float-photographer-modal-container">

                {/* ── Booking info ── */}
                <BookingInfoHeader booking={booking} />

                <div className="hourly-modal-body">
                    <EditingBookingSummary videoPackages={packages} />

                    <div className="hourly-float-plan-section">
                        <span className="hourly-float-section-label">Select plan to assign</span>
                        <div className="plan-selection-radios" role="group" aria-label="Select plan">
                            {packages.map((pkg, idx) => {
                                const rawPkgsForType = (booking?.videoPackages || []).filter(
                                    (p) =>
                                        (p.type || p.planCategory || "")
                                            .toString()
                                            .trim()
                                            .toLowerCase() ===
                                        (pkg.type || "").toString().trim().toLowerCase()
                                );
                                const isReserved = rawPkgsForType.some(
                                    (p) => p.assignedEditors?.length > 0
                                );
                                return (
                                    <label key={idx}>
                                        <input
                                            type="radio"
                                            name="packageSelect"
                                            checked={selectedPackageIndex === idx}
                                            onChange={() => setSelectedPackageIndex(idx)}
                                        />
                                        {pkg.type}
                                        {isReserved ? (
                                            <span className="plan-reserved-badge">Reserved</span>
                                        ) : null}
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div className="hourly-float-staff-section">
                    <form id="floatForm" onSubmit={handleSubmit} className="hourly-form hourly-form--roles">
                        {ROLES.map((role) => (
                            <RoleAccordion
                                key={role.key}
                                roleKey={role.key}
                                title={role.title}
                                bookingId={booking?.bookingId}
                                isOpen={isOpen}
                                services={role.showFilters ? services : []}
                                fetchListAPI={role.fetchListAPI}
                                fetchAllAPI={role.fetchAllAPI}
                                fetchSortAPI={role.fetchSortAPI}
                                showFilters={role.showFilters}
                                onSelectionChange={handleRoleSelectionChange}
                                videoPackages={packages}
                                selectedPackageIndex={selectedPackageIndex}
                                allPackagesSelections={allPackagesSelections}
                                refreshVersion={refreshVersion}
                                onAction={handleRoleSubmit}
                            />
                        ))}
                    </form>
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className="hourly-modal-footer">
                    <button
                        className="btn-secondary"
                        type="button"
                        onClick={() => { resetAll(); onClose(); }}
                    >
                        Close
                    </button>
                </div>

            </div>
        </div>
    );
};

export default EditingFloatBookingModal;
